/**
 * 재고 상세 — /preview/inventory/products/:id (hcltech-parts V장 "재고 상세 — 파트넘버 하나" · 회의 2026-10-21 §15 · 2026-10-26).
 * 제품 하나의 재고 흐름 — 머리(파트넘버 · 품목 · 제품번호) · 총합(상태별 수 열 칸) · 입고(구매 줄, 다 나간 것까지) · 출고(출고 · 임대 사건) · 실물.
 * 컨디션은 페이지 안 필터. 판매 데이터(회사 · 판매가 · 담당자)는 판매 현황이 서면 — 지금은 출고 표의 빈 열.
 * 재고 쪽 표(구매 건 · 변동표 · 실물 · 이력)의 파트넘버가 stockDetailHrefOf 로 이 페이지를 새 탭에 연다.
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import type { ProductDetail, StockLineView, StockSummaryView, UnitEventLineView, UnitView } from '../api/client';
import { PRODUCT_SHAPE_WORD, productShapeOf } from '../api/client';
import { renderPagerLine } from '../components/pager-line';
import { ROW_NO_HEAD, rowNoCell } from '../components/row-no';
import { assert } from '../lib/assert';
import { EVENT_WORD, pnTextOf, renderUnitTable } from '../components/unit-table';
import { escapeHtml, fmtDateTime } from '../lib/cells';
import { costTextOf } from '../lib/money';
import { adjustConditionsOf, adjustInputOrNullOf, adjustLotsOf, adjustQuantityHintOf, defaultLotKeyOf, newAdjustDraft, renderAdjustDialog } from '../components/stock-adjust-dialog';
import type { AdjustDraft } from '../components/stock-adjust-dialog';
import { LOT_COUNT_HEAD, STOCK_LINE_SOURCE_WORD, lotCountCells } from './InventoryView';

interface StockDetailData {
  id: string;                         // hclt_product_id — 주소의 :id
  product: ProductDetail | null;      // 머리 — 받기 전엔 null
  condition: string;                  // '' = 전부. 컨디션 필터
  lineKey: string;                    // 재고 목록에서 누른 로트(주소 ?line=) — [재고 조정] 이 먼저 골라 둔다. '' = 없음(2026-11-04)
  summary: StockSummaryView | null;   // 총합 — 서버가 센다. 컨디션을 바꾸면 다시 받는 동안 null
  summaryError: string;               // 총합만 못 받았을 때(컨디션 바꿈) — 절 안에 글 하나
  units: UnitView[];                  // 실물 — 그 제품의 부모 없는 실물(나간 것 빼고)
  inbound: StockLineView[];           // 입고 — 구매 줄(include_gone)
  outbound: UnitEventLineView[];      // 출고 — 출고 · 임대 사건
  unitOffset: number;                 // 절마다 지금 쪽의 시작(받은 줄을 화면이 SECTION_PAGE_SIZE 씩 나눈다, 2026-10-26)
  inboundOffset: number;
  outboundOffset: number;
  bTruncated: boolean;                // 셋 중 하나라도 한 쪽(500)을 넘었다 — 머리에 "앞 500"
  loading: boolean;
  error: string;
  adjust: AdjustDraft | null;        // [재고 조정] 창(2026-11-03). null = 닫힘
  adjusting: boolean;                 // 조정 보내는 중 — [조정] 잠금
  adjustError: string;                // 조정이 안 됐을 때 글(머리 아래 한 줄). '' = 없음
}

const STOCK_DETAIL_HREF = '/preview/inventory/products';
const NOT_FOUND_PREFIX = 'HTTP 404';
const OUTBOUND_TYPES: api.EInventoryEventType[] = ['dispatched', 'rented'];   // 출고 절 — 나간 것(출고 · 임대). 반품 · 이탈은 이력 탭에서
const SECTION_PAGE_SIZE = 20;   // 절 한 쪽 줄 수 — 실물 · 입고 · 출고 같게(현민 2026-10-26)
/** 총합 한 칸 — 열한 칸(현민 2026-10-28 · 자산 2026-10-29 · 준비 · 출고 예정 2026-11-03 — 묶인 판매의 상태로 가른다) */
type SummaryCountKey = Exclude<keyof StockSummaryView, 'hclt_product_id' | 'condition'>;
interface SummaryRowDef { code: string; word: string; note: string; key: SummaryCountKey }
const SUMMARY_ROWS: readonly SummaryRowDef[] = [
  { code: 'Registered', word: '전체',    note: '전체 합산', key: 'registered' },
  { code: 'Current',    word: '보유',    note: '나머지 재고 종류의 합산 − 출고', key: 'current' },
  { code: 'Inspecting', word: '입고',    note: '발주했지만 아직 입고되지 않음', key: 'incoming' },
  { code: 'Available',  word: '가용',    note: '바로 판매 · 사용 가능한 수량', key: 'available' },
  { code: 'Preparing',  word: '준비',    note: '판매 등록 판매에 묶인 실물', key: 'preparing' },
  { code: 'Reserved',   word: '출고 예정', note: '출고 요청 · 세팅 완료 판매에 묶인 실물', key: 'outgoing' },
  { code: 'Rented',     word: '임대',    note: '임대 반출 중', key: 'rented' },
  { code: 'Asset',      word: '자산',    note: '내부에서 쓰는 장 — 가용 · 판매에서 빠진다', key: 'asset' },
  { code: 'Dispatched', word: '출고 완료', note: '판매 출고 완료', key: 'dispatched' },
  { code: 'Retired',    word: '사용 불가', note: '판매 · 사용 제외', key: 'retired' },
  { code: 'Holding',    word: '홀딩',    note: '영업담당자가 판매를 위해 선점', key: 'held' },
];
type SectionPageAction = 'unit-page' | 'inbound-page' | 'outbound-page';   // 절마다 쪽 넘김 액션 — 핸들러 하나가 이 이름으로 절을 가른다
/** 글 칸 — null 이면 "—" */
const textOrDash = (text: string | null): string => (text === null ? '—' : escapeHtml(text));

/** 조정 창 칸 하나 → 초안의 고칠 칸. 액션 이름이 칸을 가른다 */
function adjustPatchOf(action: string | undefined, value: string): Partial<AdjustDraft> {
  switch (action) {
    case 'adjust-kind': {
      const kind = api.E_STOCK_ADJUST_KINDS.find((k) => k === value);
      assert(kind !== undefined, `조정 종류 라디오의 값은 E_STOCK_ADJUST_KINDS 중 하나 — renderAdjustDialog 가 그렸다: ${value}`);
      return { kind };
    }
    case 'adjust-reason': {
      const reason = api.E_RETIRED_REASONS.find((r) => r === value);
      assert(reason !== undefined, `사유 라디오의 값은 E_RETIRED_REASONS 중 하나: ${value}`);
      return { reason };
    }
    case 'adjust-condition': return { condition: value, line_key: '' };   // 로트는 컨디션마다 다르다 — handleAdjustChange 가 다시 골라 둔다
    case 'adjust-lot': return { line_key: value };
    case 'adjust-to-condition': return { to_condition: value };
    case 'adjust-quantity': return { quantity: value };
    case 'adjust-note': return { note: value };
    default: throw new Error(`조정 창에 없는 칸: ${String(action)}`);
  }
}

/** 재고 상세의 주소 — 네 표의 파트넘버 링크가 이 하나로. 컨디션을 주면 그걸 먼저 고른 채 연다('' = 전부 — 회의 2026-10-28 §0 6) */
export function stockDetailHrefOf(productId: string, condition: string, lineKey: string): string {
  const base = `${STOCK_DETAIL_HREF}/${encodeURIComponent(productId)}`;
  const params = new URLSearchParams();
  if (condition !== '') params.set('condition', condition);
  if (lineKey !== '') params.set('line', lineKey);   // 누른 로트 — [재고 조정] 이 먼저 골라 둔다(2026-11-04)
  const query = params.toString();
  return query === '' ? base : `${base}?${query}`;
}

export class StockDetailView extends SolaView<StockDetailData> {
  override events = {
    click:  { '@unit-page': 'handleSectionPage', '@inbound-page': 'handleSectionPage', '@outbound-page': 'handleSectionPage',
              '@adjust-open': 'handleAdjustOpen', '@adjust-cancel': 'handleAdjustCancel', '@adjust-confirm': 'handleAdjustConfirm' },
    change: { '@condition': 'handleCondition', '@adjust-kind': 'handleAdjustChange', '@adjust-condition': 'handleAdjustChange', '@adjust-lot': 'handleAdjustChange', '@adjust-reason': 'handleAdjustChange', '@adjust-to-condition': 'handleAdjustChange' },
    input:  { '@adjust-quantity': 'handleAdjustInput', '@adjust-note': 'handleAdjustInput' },
  };

  constructor(params: { id: string; condition: string; lineKey: string }) {
    super();
    this.data = { id: params.id, product: null, condition: params.condition, lineKey: params.lineKey, summary: null, summaryError: '', units: [], inbound: [], outbound: [], unitOffset: 0, inboundOffset: 0, outboundOffset: 0, bTruncated: false, loading: true, error: '', adjust: null, adjusting: false, adjustError: '' };
  }

  override getTitle(): string {
    const product = this.data.product;
    return product === null ? '재고 상세 — HCLTech' : `재고 상세 ${pnTextOf(product)} — HCLTech`;
  }

  override onMount(): void {
    void this.load();
  }

  /**
   * 다섯을 한 번에 받는다 — 제품(fetchProduct) · 총합(fetchStockSummary, 컨디션 전부) · 실물(fetchUnits product_id, 나간 것 빼고) ·
   * 입고(fetchStockLines product_id · include_gone) · 출고(fetchUnitEvents product_id · types dispatched · rented, 기간 없음).
   * 하나라도 실패하면 error 글 하나(404 면 "없는 제품") — 받은 것만 그리지 않는다. 각 목록은 한 쪽(500)까지, 넘으면 머리에 "앞 500" 표시.
   * 컨디션은 서버가 거르지 않는다 — 받은 줄을 화면이 거른다(handleCondition)
   */
  private async load(): Promise<void> {
    const id = this.data.id;
    try {
      const [product, summary, units, inbound, outbound] = await Promise.all([
        api.fetchProduct(id),
        api.fetchStockSummary(id, this.data.condition),
        api.fetchUnits({ status: '', q: '', show_gone: false, product_id: id, offset: 0 }),
        api.fetchStockLines({ q: '', product_id: id, include_gone: true, category: '', from: '', to: '', offset: 0 }),
        api.fetchUnitEvents({ q: '', types: OUTBOUND_TYPES, reason: '', from: '', to: '', product_id: id, stocktake_id: '', offset: 0 }),
      ]);
      const bTruncated = units.total > units.rows.length || inbound.total > inbound.rows.length || outbound.total > outbound.rows.length;
      this.data = { ...this.data, product, summary, units: units.rows, inbound: inbound.rows, outbound: outbound.rows, bTruncated, loading: false };
    } catch (e) {
      this.data = { ...this.data, loading: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.refreshRegion('.pv-body', () => this.renderBody());
  }

  /** 컨디션 고르기 — '' = 전부. 후보는 받은 실물 · 입고 · 출고 줄의 컨디션 합집합. 세 절 · 머리는 받은 줄로 다시 그리고, 총합만 서버에 다시 묻는다 */
  handleCondition(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, condition: (el as HTMLSelectElement).value, summary: null, summaryError: '', unitOffset: 0, inboundOffset: 0, outboundOffset: 0 };   // 거른 줄이 바뀌면 세 절 다 첫 쪽
    this.refreshRegion('.pv-body', () => this.renderBody());
    void this.loadSummary();
  }
  /** 총합 다시 받기 — 받는 사이 컨디션이 또 바뀌었으면 늦게 온 답은 버린다 */
  private async loadSummary(): Promise<void> {
    const condition = this.data.condition;
    try {
      const summary = await api.fetchStockSummary(this.data.id, condition);
      if (condition !== this.data.condition) return;
      this.data = { ...this.data, summary };
    } catch (e) {
      if (condition !== this.data.condition) return;
      this.data = { ...this.data, summaryError: e instanceof Error ? e.message : String(e) };
    }
    this.refreshRegion('.pd-summary', () => this.renderSummaryBody());
  }
  // ─── 재고 조정 — 수량 맞추기(2026-11-03) ───
  /** 창 열기 — 컨디션은 지금 고른 것('' 면 비워 두고 창에서 고른다) */
  handleAdjustOpen(): void {
    this.data = { ...this.data, adjust: newAdjustDraft(this.data.condition, this.data.inbound, this.data.lineKey), adjustError: '' };
    this.refreshRegion('.pv-body', () => this.renderBody());
  }

  handleAdjustCancel(): void {
    if (this.data.adjusting) return;
    this.data = { ...this.data, adjust: null };
    this.refreshRegion('.pv-body', () => this.renderBody());
  }

  /** 라디오 · 셀렉트 — 칸 모양이 바뀌니(사유 · 바꿀 컨디션) 다시 그린다 */
  handleAdjustChange(_e: Event, el: HTMLElement): void {
    const draft = this.data.adjust;
    assert(draft !== null, '조정 창의 칸은 창이 열려 있을 때만 있다');
    const value = (el as HTMLInputElement | HTMLSelectElement).value;
    const patched: AdjustDraft = { ...draft, ...adjustPatchOf(el.dataset.action, value) };
    const lineKey = patched.line_key === '' ? defaultLotKeyOf(adjustLotsOf(this.data.inbound, patched.condition), this.data.lineKey) : patched.line_key;   // 컨디션을 바꾸면 그 컨디션의 로트를 다시 골라 둔다
    this.data = { ...this.data, adjust: { ...patched, line_key: lineKey } };
    this.refreshRegion('.pv-body', () => this.renderBody());
  }

  /** 글자마다(수량 · 글) — 다시 그리면 포커스가 날아가니 [조정] 잠김만 손으로 */
  handleAdjustInput(_e: Event, el: HTMLElement): void {
    const draft = this.data.adjust;
    assert(draft !== null, '조정 창의 칸은 창이 열려 있을 때만 있다');
    const next: AdjustDraft = { ...draft, ...adjustPatchOf(el.dataset.action, (el as HTMLInputElement).value) };
    this.data = { ...this.data, adjust: next };
    const button = this._rootElement?.querySelector<HTMLButtonElement>('button[data-action="adjust-confirm"]');
    if (button) button.disabled = adjustInputOrNullOf(next, this.data.id) === null || this.data.adjusting;
    const hint = this._rootElement?.querySelector<HTMLElement>('.pv-adjust-hint');
    if (hint) hint.textContent = adjustQuantityHintOf(next);
  }

  /** [조정] — 한 번에 보낸다. 되면 창을 닫고 다섯을 다시 받는다, 안 되면 창을 닫고 머리 아래 한 줄(모자람 409 등 — 한 장도 안 바뀐다) */
  async handleAdjustConfirm(): Promise<void> {
    const draft = this.data.adjust;
    const input = draft === null ? null : adjustInputOrNullOf(draft, this.data.id);
    assert(input !== null, '[조정] 은 창이 열려 있고 칸이 다 찼을 때만 켜진다 — renderAdjustDialog 가 disabled 를 붙였다');
    this.data = { ...this.data, adjusting: true };
    this.refreshRegion('.pv-body', () => this.renderBody());
    try {
      await api.adjustStock(input);
      this.data = { ...this.data, adjusting: false, adjust: null, adjustError: '', summary: null };
      await this.load();
    } catch (e) {
      this.data = { ...this.data, adjusting: false, adjust: null, adjustError: `재고 조정 안 됨(한 장도 안 바뀜) — ${e instanceof Error ? e.message : String(e)}` };
      this.refreshRegion('.pv-body', () => this.renderBody());
    }
  }

  /** 절 쪽 넘김 — 버튼의 액션 이름으로 절을 가르고 data-offset 으로 옮긴다. 다시 받지 않는다 */
  handleSectionPage(_e: Event, el: HTMLElement): void {
    const action = el.dataset.action as SectionPageAction;
    const offset = Number(el.dataset.offset);
    assert(Number.isInteger(offset) && offset >= 0, '페이저 버튼은 data-offset(그 쪽의 offset)을 가진다 — renderPagerLine 이 붙였다');
    switch (action) {
      case 'unit-page':     this.data = { ...this.data, unitOffset: offset }; break;
      case 'inbound-page':  this.data = { ...this.data, inboundOffset: offset }; break;
      case 'outbound-page': this.data = { ...this.data, outboundOffset: offset }; break;
      default: throw new Error(`unhandled switch case: ${action satisfies never}`);
    }
    this.refreshRegion('.pv-body', () => this.renderBody());
  }


  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-stock-detail pv-page">
        <header class="page-header"><h1>📦 재고 상세</h1></header>
        <section class="page-body pv-body">${this.renderBody()}</section>
      </div>`;
  }

  /** 본문 — 머리 + 절 넷(총합 · 입고 · 출고 · 실물 — 현민 2026-10-28). 받는 중 · 실패면 글 하나 */
  private renderBody(): string {
    const { product, loading, error } = this.data;
    if (loading) return '<p class="text-muted">불러오는 중…</p>';
    if (error.startsWith(NOT_FOUND_PREFIX)) return `<p class="text-muted">없는 제품입니다 — ${escapeHtml(this.data.id)}</p>`;
    if (error !== '' || product === null) return `<p class="text-muted">불러오지 못했습니다 — ${escapeHtml(error)}</p>`;
    const adjustError = this.data.adjustError === '' ? '' : `<p class="pv-action-error">${escapeHtml(this.data.adjustError)}</p>`;
    const adjustDialog = this.data.adjust === null ? '' : renderAdjustDialog(this.data.adjust, adjustConditionsOf(this.conditionsOf()), this.data.inbound, this.data.id, this.data.adjusting);
    return `${this.renderHead(product)}${adjustError}${adjustDialog}${this.renderSummarySection()}${this.renderInboundSection()}${this.renderOutboundSection()}${this.renderUnitSection()}`;
  }

  /** 머리 — 파트넘버 · 품목 · 제품번호(제품 상세 새 탭) · 컨디션 고르기. 수는 총합 절에만(보유 · 가용 칩은 총합과 겹쳐 뺌, 2026-10-30) */
  private renderHead(product: ProductDetail): string {
    const options = ['', ...this.conditionsOf()].map((condition) => `<option value="${escapeHtml(condition)}" ${condition === this.data.condition ? 'selected' : ''}>${condition === '' ? '컨디션 전부' : escapeHtml(condition)}</option>`).join('');
    return `
      <div class="form-card pd-head">
        <div class="pd-head-row">
          <div class="pd-title">
            <span class="pd-pn mono">${escapeHtml(pnTextOf(product))}</span>
            <span class="pd-desc">${product.description === null ? '<span class="text-muted">설명 없음</span>' : escapeHtml(product.description)}</span>
          </div>
          <div class="pd-head-right">
            <select class="pc-select" data-action="condition" aria-label="컨디션">${options}</select>
            <button type="button" class="btn-ghost" data-action="adjust-open" title="수량 맞추기 — 빼기 · 넣기 · 컨디션 변경">재고 조정</button>
            <a class="pl-id mono" href="/products/${encodeURIComponent(product.id)}" target="_blank" rel="noopener" title="제품 상세">${escapeHtml(product.hclt_number)}</a>
          </div>
        </div>
        <div class="pd-chips">
          <span class="chip chip-muted">${PRODUCT_SHAPE_WORD[productShapeOf(product.items.length)]}</span>
          ${product.spec_category === null ? '' : `<span class="chip chip-muted">${escapeHtml(product.spec_category)}</span>`}
          ${this.data.bTruncated ? '<span class="chip chip-muted" title="목록마다 앞 500줄까지만 받았다 — 수는 받은 줄로 센다">앞 500</span>' : ''}
        </div>
      </div>`;
  }

  // ─── 컨디션으로 거른 줄(받은 줄을 화면이 거른다) ───
  private unitsOfCondition(): UnitView[] {
    const condition = this.data.condition;
    return condition === '' ? this.data.units : this.data.units.filter((unit) => unit.condition === condition);
  }

  private inboundOfCondition(): StockLineView[] {
    const condition = this.data.condition;
    return condition === '' ? this.data.inbound : this.data.inbound.filter((line) => line.condition === condition);
  }

  private outboundOfCondition(): UnitEventLineView[] {
    const condition = this.data.condition;
    return condition === '' ? this.data.outbound : this.data.outbound.filter((event) => event.condition === condition);
  }

  /** 컨디션 후보 — 받은 실물 · 입고 · 출고 줄의 컨디션 합집합(글 순) */
  private conditionsOf(): string[] {
    const all = [...this.data.units.map((unit) => unit.condition), ...this.data.inbound.map((line) => line.condition), ...this.data.outbound.map((event) => event.condition)];
    return [...new Set(all)].sort();
  }

  /** 총합 절 — 상태별 수 열한 칸(고른 컨디션 기준) */
  private renderSummarySection(): string {
    return `
      <section class="form-card pd-card">
        <h2>총합 <small>${this.data.condition === '' ? '컨디션 전부' : escapeHtml(this.data.condition)} — 서버에 붙은 부품은 서버가 센다</small></h2>
        <div class="pd-summary">${this.renderSummaryBody()}</div>
      </section>`;
  }

  /** 총합 칸 줄 — 상태 하나 = 칸 하나, 가로로 열한 칸(현민 2026-10-28 · 자산 2026-10-29). 뜻은 칸에 마우스를 올리면. 받는 중이면 수가 "…", 못 받았으면 글 하나 */
  private renderSummaryBody(): string {
    const { summary, summaryError } = this.data;
    if (summaryError !== '') return `<p class="text-muted">총합을 불러오지 못했습니다 — ${escapeHtml(summaryError)}</p>`;
    const tiles = SUMMARY_ROWS.map((row) => {
      const count = summary === null ? '…' : String(summary[row.key]);
      return `
        <div class="sd-sum-tile" title="${escapeHtml(row.note)}">
          <span class="sd-sum-code">${row.code}</span>
          <span class="sd-sum-word">${row.word}</span>
          <span class="sd-sum-count">${count}</span>
        </div>`;
    }).join('');
    return `<div class="sd-summary">${tiles}</div>`;
  }

  /** 실물 절 — 지금 우리한테 있는 장(나간 것 빼고). 표는 재고 현황 실물 탭과 같은 한 벌(읽기 꼴) */
  private renderUnitSection(): string {
    const units = this.unitsOfCondition();
    return `
      <section class="form-card pd-card">
        <h2>실물 <small>${units.length}장 — 검수 중 · 가용 · 세팅됨 · 임대 · 자산</small></h2>
        ${renderUnitTable(pageOf(units, this.data.unitOffset), { kind: 'read' }, '지금 있는 실물이 없습니다', this.data.unitOffset)}
        ${pagerOf(units.length, this.data.unitOffset, 'unit-page')}
      </section>`;
  }

  /** 입고 절 — 구매 줄 하나 = 한 줄, 다 나간 줄(보유 0)까지. 최근 구매 먼저(서버 정렬) */
  private renderInboundSection(): string {
    const lines = this.inboundOfCondition();
    const offset = this.data.inboundOffset;
    const rows = pageOf(lines, offset).map((line, index) => `
      <tr>
        ${rowNoCell(offset, index)}
        <td class="pl-sub mono">${textOrDash(line.purchased_date)}</td>
        <td class="pl-sub">${textOrDash(line.supplier_name)}</td>
        <td class="pl-sub">${escapeHtml(line.condition)}</td>
        ${lotCountCells(line)}
        <td class="pl-sub mono">${costTextOf(line.unit_price, line.cost_currency)}</td>
        <td class="pl-sub">${textOrDash(line.buyer_name)}</td>
        <td class="pl-sub mono">${textOrDash(line.purchase_no)}</td>
        <td class="pl-sub"><span class="chip">${STOCK_LINE_SOURCE_WORD[line.source]}</span></td>
      </tr>`).join('');
    return `
      <section class="form-card pd-card">
        <h2>입고 <small>구매 줄 ${lines.length} — 다 나간 줄까지</small></h2>
        <table class="pv-table pv-lines">
          <thead><tr>${ROW_NO_HEAD}<th>구매일</th><th>구매처</th><th>컨디션</th>${LOT_COUNT_HEAD}<th>단가</th><th>담당자</th><th>구매번호</th><th>출처</th></tr></thead>
          <tbody>${rows || '<tr><td colspan="12" class="text-muted">구매 기록이 없습니다</td></tr>'}</tbody>
        </table>
        ${pagerOf(lines.length, offset, 'inbound-page')}
      </section>`;
  }

  /** 출고 절 — 출고 · 임대 사건 한 줄 = 한 줄(최신순). 판매(회사 · 판매가 · 담당자)는 판매 현황이 서면 — 지금은 빈 열 */
  private renderOutboundSection(): string {
    const events = this.outboundOfCondition();
    const offset = this.data.outboundOffset;
    const rows = pageOf(events, offset).map((event, index) => `
      <tr>
        ${rowNoCell(offset, index)}
        <td class="pl-sub mono">${escapeHtml(fmtDateTime(event.created_at))}</td>
        <td class="pl-main"><span class="chip pv-ev-${event.type}">${escapeHtml(EVENT_WORD[event.type])}</span></td>
        <td class="pl-sub mono">${textOrDash(event.serial_number)}</td>
        <td class="pl-sub">${escapeHtml(event.condition)}</td>
        <td class="pl-sub">${textOrDash(event.changed_by)}</td>
        <td class="pl-sub text-muted">—</td>
        <td class="pl-sub pl-id mono"><a href="/inventory/units/${event.unit_id}" target="_blank" rel="noopener" title="실물 상세">#${event.unit_id}</a></td>
      </tr>`).join('');
    return `
      <section class="form-card pd-card">
        <h2>출고 <small>${events.length}건 — 출고 · 임대</small></h2>
        <table class="pv-table pv-events">
          <thead><tr>${ROW_NO_HEAD}<th>시각</th><th>무슨 일</th><th>시리얼</th><th>컨디션</th><th>누가</th><th title="판매 현황이 서면 회사 · 판매가 · 담당자">판매(준비 중)</th><th>실물</th></tr></thead>
          <tbody>${rows || '<tr><td colspan="8" class="text-muted">나간 기록이 없습니다</td></tr>'}</tbody>
        </table>
        ${pagerOf(events.length, offset, 'outbound-page')}
      </section>`;
  }
}

/** 받은 줄 중 지금 쪽 — offset 부터 SECTION_PAGE_SIZE 줄 */
function pageOf<T>(rows: readonly T[], offset: number): T[] {
  return rows.slice(offset, offset + SECTION_PAGE_SIZE);
}

/** 절 쪽 넘김 줄 — 한 쪽에 다 들어가면 안 그린다 */
function pagerOf(total: number, offset: number, action: SectionPageAction): string {
  if (total <= SECTION_PAGE_SIZE) return '';
  return renderPagerLine({ total, limit: SECTION_PAGE_SIZE, offset }, action);
}
